import React, { useState, useEffect } from 'react';
import { useToast } from '../../context/ToastContext';
import { Card, Button, Badge, ProgressBar, Modal, SectionHeader, Spinner, AIThinking } from '../../components/shared/UI';
import API from '../../utils/apiClient';

const LEVELS = ['A1','A2','B1','B2'];
const statusColors = {
  not_started: 'bg-gray-100 text-gray-500',
  in_progress: 'bg-gold-100 text-de-black',
  complete:    'bg-green-100 text-green-800',
};

export default function AdminCurriculumPage() {
  const { addToast } = useToast();
  const [view, setView]         = useState('overview'); // 'overview' | 'manage'
  const [topics, setTopics]     = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [addModal, setAddModal] = useState(false);
  const [completing, setCompleting] = useState('');
  const [editTopic, setEditTopic]   = useState(null);
  const [form, setForm] = useState({ title:'', description:'', level:'A1' });

  useEffect(() => {
    Promise.all([
      API.get('/curriculum/admin/overview'),
    ]).then(([{ data }]) => {
      setTopics(data.topics || []);
      setStudents(data.students || []);
    }).catch(() => addToast('Failed to load curriculum data','error'))
    .finally(() => setLoading(false));
  }, []);

  const refreshOverview = () => {
    setLoading(true);
    API.get('/curriculum/admin/overview')
      .then(({ data }) => { setTopics(data.topics||[]); setStudents(data.students||[]); })
      .finally(() => setLoading(false));
  };

  const createTopic = async () => {
    if (!form.title) { addToast('Title required','error'); return; }
    try {
      await API.post('/curriculum', form);
      setAddModal(false);
      setForm({ title:'', description:'', level:'A1' });
      refreshOverview();
      addToast('Topic created!','success');
    } catch (err) { addToast(err.message||'Failed','error'); }
  };

  const deleteTopic = async (id, title) => {
    if (!window.confirm(`Delete topic "${title}"?`)) return;
    try {
      await API.delete(`/curriculum/${id}`);
      refreshOverview();
      addToast('Topic deleted','success');
    } catch (err) { addToast(err.message||'Failed','error'); }
  };

  const markComplete = async (studentId, topicId, topicTitle) => {
    const key = `${studentId}-${topicId}`;
    setCompleting(key);
    try {
      const { data } = await API.put(`/curriculum/student/${studentId}/topic/${topicId}/complete`);
      addToast(`✅ ${topicTitle} marked complete! ${data.flashcardsGenerated} AI flashcards generated.`,'success');
      refreshOverview();
    } catch (err) { addToast(err.message||'Failed','error'); }
    finally { setCompleting(''); }
  };

  const topicsByLevel = LEVELS.reduce((acc, l) => {
    acc[l] = topics.filter(t => t.level === l);
    return acc;
  }, {});

  if (loading) return (
    <div className="flex items-center justify-center py-24"><Spinner size="lg" className="text-brand-500" /></div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-de-black">Curriculum Management</h1>
          <p className="text-sm text-gray-500">Track student progress and manage topics</p>
        </div>
        <div className="flex gap-2">
          <div className="flex gap-1 p-1 bg-surface rounded-xl">
            {['overview','manage'].map(v => (
              <button key={v} onClick={()=>setView(v)}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium capitalize transition-all
                  ${view===v ? 'bg-white text-de-black shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                {v}
              </button>
            ))}
          </div>
          <Button variant="primary" onClick={()=>setAddModal(true)}>+ Add Topic</Button>
        </div>
      </div>

      {/* ── OVERVIEW: student progress per topic ─────────────── */}
      {view === 'overview' && (
        <div>
          {students.length === 0 ? (
            <p className="text-center text-gray-400 py-12">No students enrolled yet.</p>
          ) : (
            LEVELS.map(level => {
              const levelTopics = topicsByLevel[level];
              if (!levelTopics.length) return null;
              return (
                <div key={level} className="mb-8">
                  <h2 className="text-lg font-bold text-de-black mb-4 flex items-center gap-2">
                    <span className="px-3 py-1 bg-brand-500 text-white rounded-lg text-sm">Level {level}</span>
                    <span className="text-sm font-normal text-gray-500">{levelTopics.length} topics</span>
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-max">
                      <thead>
                        <tr className="border-b-2 border-gray-200">
                          <th className="text-left py-3 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wide">Student</th>
                          {levelTopics.map(t => (
                            <th key={t.id} className="text-center py-3 px-3 font-semibold text-gray-500 text-xs max-w-[100px]">
                              <span className="block truncate max-w-[90px] mx-auto" title={t.title}>{t.title}</span>
                            </th>
                          ))}
                          <th className="text-center py-3 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wide">Progress</th>
                        </tr>
                      </thead>
                      <tbody>
                        {students.filter(s => (s.unlockedLevels||[]).includes(level)).map(student => (
                          <tr key={student.id} className="border-b border-gray-50 hover:bg-surface transition-colors">
                            <td className="py-3 px-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-brand-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                                  {student.name.charAt(0)}
                                </div>
                                <span className="font-medium text-de-black text-xs">{student.name}</span>
                              </div>
                            </td>
                            {levelTopics.map(topic => {
                              const status = student.topicProgress?.[topic.id] || 'not_started';
                              const key = `${student.id}-${topic.id}`;
                              const isLoading = completing === key;
                              return (
                                <td key={topic.id} className="py-3 px-3 text-center">
                                  {status === 'complete' ? (
                                    <span className="text-green-600 text-base">✅</span>
                                  ) : (
                                    <button
                                      onClick={() => markComplete(student.id, topic.id, topic.title)}
                                      disabled={!!completing}
                                      title="Mark complete"
                                      className="w-7 h-7 rounded-full border-2 border-gray-200 hover:border-brand-500 hover:bg-brand-50 transition-all text-xs text-gray-400 hover:text-brand-500 inline-flex items-center justify-center">
                                      {isLoading ? <Spinner size="xs" className="text-brand-500" /> : '○'}
                                    </button>
                                  )}
                                </td>
                              );
                            })}
                            <td className="py-3 px-4">
                              <div className="w-28">
                                <ProgressBar value={student.topicProgress ? Object.values(student.topicProgress).filter(s=>s==='complete').length : 0}
                                  max={levelTopics.length} color="red" showPercent={false} />
                                <p className="text-xs text-gray-400 text-center mt-1">
                                  {student.topicProgress ? Object.values(student.topicProgress).filter(s=>s==='complete').length : 0}/{levelTopics.length}
                                </p>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ── MANAGE: topic CRUD ────────────────────────────────── */}
      {view === 'manage' && (
        <div>
          {LEVELS.map(level => (
            <div key={level} className="mb-8">
              <h2 className="text-base font-bold text-de-black mb-3 flex items-center gap-2">
                <span className="px-2 py-0.5 bg-brand-500 text-white rounded text-sm">Level {level}</span>
              </h2>
              {topicsByLevel[level].length === 0 ? (
                <p className="text-sm text-gray-400 pl-2">No topics yet for {level}.</p>
              ) : (
                <div className="space-y-2">
                  {topicsByLevel[level].map(topic => (
                    <div key={topic.id} className="flex items-center justify-between p-4 bg-surface rounded-xl hover:bg-gray-100 transition-colors">
                      <div>
                        <p className="text-sm font-semibold text-de-black">{topic.title}</p>
                        {topic.description && <p className="text-xs text-gray-500 mt-0.5">{topic.description}</p>}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="secondary" onClick={() => setEditTopic(topic)}>Edit</Button>
                        <Button size="sm" variant="danger" onClick={() => deleteTopic(topic.id, topic.title)}>Delete</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Topic Modal */}
      <Modal isOpen={addModal} onClose={() => setAddModal(false)} title="Add Curriculum Topic" size="sm">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-de-black mb-1.5">Topic Title</label>
            <input type="text" value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))}
              placeholder="e.g. Konjunktiv II"
              className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-de-black mb-1.5">Description</label>
            <textarea value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))}
              placeholder="Brief description of what this topic covers"
              rows={3} className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none resize-none" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-de-black mb-1.5">Level</label>
            <select value={form.level} onChange={e=>setForm(p=>({...p,level:e.target.value}))}
              className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none bg-white">
              {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div className="p-3 bg-gold-50 border border-gold-200 rounded-xl">
            <p className="text-xs text-de-black font-semibold mb-1">🤖 AI Flashcard Generation</p>
            <p className="text-xs text-gray-600">When you mark this topic complete for a student, Claude AI will automatically generate 15 mixed flashcard questions (vocab + grammar) for them to practice.</p>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={() => setAddModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={createTopic}>Create Topic</Button>
          </div>
        </div>
      </Modal>

      {/* Edit Topic Modal */}
      <Modal isOpen={!!editTopic} onClose={() => setEditTopic(null)} title="Edit Topic" size="sm">
        {editTopic && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-de-black mb-1.5">Title</label>
              <input type="text" value={editTopic.title} onChange={e => setEditTopic(p=>({...p,title:e.target.value}))}
                className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-de-black mb-1.5">Description</label>
              <textarea value={editTopic.description||''} onChange={e => setEditTopic(p=>({...p,description:e.target.value}))}
                rows={3} className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none resize-none" />
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="secondary" onClick={() => setEditTopic(null)}>Cancel</Button>
              <Button variant="primary" onClick={async () => {
                try {
                  await API.put(`/curriculum/${editTopic.id}`, editTopic);
                  refreshOverview();
                  setEditTopic(null);
                  addToast('Topic updated','success');
                } catch (err) { addToast(err.message||'Failed','error'); }
              }}>Save</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
